import {
  Box,
  Button,
  NeoColor,
  P3,
  P4,
  Stack,
} from "@noom/wax-component-library";
import { useTranslation } from "react-i18next";

import {
  formatDateMonthDay,
  formatDateWeekdayMonthDay,
} from "../../../utils/dateUtils";
import {
  MedCtaAction,
  trackPrescriptionWebviewCtaTapped,
} from "../../nativeAnalytics/medAnalytics";
import { openDeeplink, openTrackingUrl } from "../utils";

import type { TreatmentStatusCardModel } from "./treatmentDetailViewModel";

type TreatmentStatusCardProps = {
  statusCard: TreatmentStatusCardModel;
  medicationName: string;
};

type StatusCardContent = {
  title: string;
  body?: string;
  ctaLabel?: string;
  onCta?: () => void;
  ctaAction?: MedCtaAction;
};

const isSupplementCopy = (statusCard: TreatmentStatusCardModel): boolean =>
  "copyVariant" in statusCard && statusCard.copyVariant === "supplement";

const isLabCopy = (statusCard: TreatmentStatusCardModel): boolean =>
  "copyVariant" in statusCard && statusCard.copyVariant === "lab";

const resolveStatusCardContent = (
  statusCard: TreatmentStatusCardModel,
  t: (key: string, options?: Record<string, unknown>) => string
): StatusCardContent => {
  switch (statusCard.type) {
    case "shippingSoon":
      if (isLabCopy(statusCard)) {
        return {
          title: t("detail.statusCard.lab.shippingSoon.title"),
          body: t("detail.statusCard.lab.shippingSoon.body"),
        };
      }
      return isSupplementCopy(statusCard)
        ? {
            title: t("detail.statusCard.supplement.shippingSoon.title"),
            body: t("detail.statusCard.supplement.shippingSoon.body"),
          }
        : {
            title: t("detail.statusCard.shippingSoon.title"),
            body: t("detail.statusCard.shippingSoon.body"),
          };
    case "shipped": {
      const { trackShipmentUrl } = statusCard;
      const trackCta = trackShipmentUrl
        ? {
            ctaLabel: t("detail.statusCard.shipped.cta"),
            onCta: () => openTrackingUrl(trackShipmentUrl),
            ctaAction: MedCtaAction.TrackShipment,
          }
        : {};
      if (isLabCopy(statusCard)) {
        return { title: t("detail.statusCard.lab.shipped.title"), ...trackCta };
      }
      return isSupplementCopy(statusCard)
        ? {
            title: t("detail.statusCard.supplement.shipped.title"),
            ...trackCta,
          }
        : {
            title: t("detail.statusCard.shipped.title"),
            // Fall back to the hardcoded care.json string when the backend
            // has not yet populated storage_instruction in the catalog.
            body: statusCard.storageInstruction ?? t("detail.statusCard.delivered.body"),
            ...trackCta,
          };
    }
    case "replacementShipped": {
      const { trackShipmentUrl } = statusCard;
      const trackCta = trackShipmentUrl
        ? {
            ctaLabel: t("detail.statusCard.shipped.cta"),
            onCta: () => openTrackingUrl(trackShipmentUrl),
            ctaAction: MedCtaAction.TrackShipment,
          }
        : {};
      return {
        title: isSupplementCopy(statusCard)
          ? t("detail.statusCard.supplement.replacementShipped.title")
          : t("detail.statusCard.replacementShipped.title"),
        ...trackCta,
      };
    }
    case "delivered":
      return isSupplementCopy(statusCard)
        ? {
            // Delivered has no date in GraphQL; Figma date line is unavailable.
            title: t("detail.statusCard.supplement.delivered.title"),
          }
        : {
            title: t("detail.statusCard.delivered.title"),
            // Fall back to the hardcoded care.json string when the backend
            // has not yet populated storage_instruction in the catalog.
            body: statusCard.storageInstruction ?? t("detail.statusCard.delivered.body"),
          };
    case "refills": {
      const date = formatDateMonthDay(statusCard.nextRefillDate);
      return isSupplementCopy(statusCard)
        ? {
            title: t("detail.statusCard.supplement.refills.title", { date }),
            body: t("detail.statusCard.supplement.shippingSoon.body"),
          }
        : {
            title: t("detail.statusCard.refills.title", { date }),
            body: t("detail.statusCard.refills.body"),
          };
    }
    case "refillAvailable":
      return {
        title: statusCard.deadlineDate
          ? t("detail.statusCard.refillAvailable.titleWithDeadline", {
              date: formatDateMonthDay(statusCard.deadlineDate),
            })
          : t("detail.statusCard.refillAvailable.title"),
        body: statusCard.deadlineDate
          ? t("detail.statusCard.refillAvailable.bodyWithDeadline")
          : t("detail.statusCard.refillAvailable.body"),
        ctaLabel: t("detail.statusCard.refillAvailable.cta"),
        onCta: () => openDeeplink(statusCard.refillIntakeDeeplink),
        ctaAction: MedCtaAction.FinishIntake,
      };
    case "refillInReview":
      return {
        title: t("detail.statusCard.refillInReview.title"),
        body: t("detail.statusCard.refillInReview.body"),
      };
    case "labNextKitShipped": {
      const { trackShipmentUrl } = statusCard;
      const trackCta = trackShipmentUrl
        ? {
            ctaLabel: t("detail.statusCard.shipped.cta"),
            onCta: () => openTrackingUrl(trackShipmentUrl),
            ctaAction: MedCtaAction.TrackShipment,
          }
        : {};
      return {
        title: t("detail.statusCard.lab.nextKitShipped.title"),
        ...trackCta,
      };
    }
    case "labDelivered": {
      const formatted = statusCard.deliveredDate
        ? formatDateWeekdayMonthDay(statusCard.deliveredDate)
        : "";
      const keyBase = statusCard.subsequentKit
        ? "detail.statusCard.lab.subsequentDelivered"
        : "detail.statusCard.lab.delivered";
      return {
        title: formatted
          ? t(`${keyBase}.titleWithDate`, { date: formatted })
          : t(`${keyBase}.title`),
      };
    }
    case "completedKitShipped": {
      const { trackShipmentUrl } = statusCard;
      const trackCta = trackShipmentUrl
        ? {
            ctaLabel: t("detail.statusCard.shipped.cta"),
            onCta: () => openTrackingUrl(trackShipmentUrl),
            ctaAction: MedCtaAction.TrackShipment,
          }
        : {};
      return {
        title: statusCard.subsequentKit
          ? t("detail.statusCard.lab.completedTestKitShipped.title")
          : t("detail.statusCard.lab.completedKitShipped.title"),
        ...trackCta,
      };
    }
    case "completedKitReceived":
      return {
        title: statusCard.subsequentKit
          ? t("detail.statusCard.lab.completedTestKitReceived.title")
          : t("detail.statusCard.lab.completedKitReceived.title"),
      };
  }
};

export const TreatmentStatusCard = ({
  statusCard,
  medicationName,
}: TreatmentStatusCardProps) => {
  const { t } = useTranslation("care", { useSuspense: false });
  const content = resolveStatusCardContent(statusCard, t);

  return (
    <Box
      data-cy="treatment-status-card"
      width="100%"
      bg="#f4f4f4"
      borderRadius="8px"
      px={4}
      py={4}
    >
      <Stack spacing={3} alignItems="flex-start">
        <Stack spacing={1} alignItems="stretch" width="100%">
          <P3 fontWeight="medium" color={NeoColor.greyscale.black}>
            {content.title}
          </P3>
          {content.body && (
            <P4 fontWeight="normal" color={NeoColor.greyscale.gray4}>
              {content.body}
            </P4>
          )}
        </Stack>
        {content.ctaLabel && content.onCta && (
          // Figma secondary CTA fill is white @ 0% opacity → transparent on
          // the #f4f4f4 card (not a white pill).
          <Button
            data-cy="treatment-status-card-cta"
            size="sm"
            variant="solid"
            colorScheme="secondary"
            bg="transparent"
            color={NeoColor.greyscale.black}
            _hover={{ bg: "transparent" }}
            _active={{ bg: "transparent" }}
            onClick={() => {
              void trackPrescriptionWebviewCtaTapped({
                ctaText: content.ctaLabel!,
                context: "status_card",
                medicationName,
                ctaAction: content.ctaAction,
              });
              content.onCta?.();
            }}
          >
            {content.ctaLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
